/**
 * Reforma UX §6.2 — utilitários de derivação de dados da sessão.
 *
 * Runtime (SESS-09 + correção v2 §5.2.6): derivação em camadas —
 *   1. `session.runtime` top-level (NOVO, persistido pelo backend na criação;
 *      null em sessões antigas).
 *   2. `metadata.runtime` / `context.runtime` / `*.runtime_target` (string).
 *   3. Proxy por canal (fallback/heurística): `cli` = sessão iniciada em runtime
 *      externo (OpenClaw via MCP usa caller_channel 'cli'); demais canais
 *      (dashboard/telegram/voice) = RAIOS. O wireframe confirma o proxy: toda
 *      sessão OpenClaw aparece com canal CLI.
 */
import type { Session } from '@/lib/api';
import { getSessionTitle } from '@/lib/message-utils';

export interface SessionRuntime {
  key: string;
  label: string;
  /** true = runtime externo → composer read-only (SESS-09). */
  external: boolean;
}

function readString(obj: Record<string, unknown> | null | undefined, key: string): string | null {
  const v = obj?.[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export function deriveSessionRuntime(
  session: Pick<Session, 'channel' | 'metadata' | 'context'> & { runtime?: string | null },
): SessionRuntime {
  const raw =
    // §5.2.6 — runtime REAL persistido (top-level) tem precedência; heurística
    // (metadata/context/canal) vira fallback pra sessões antigas.
    (typeof session.runtime === 'string' && session.runtime.trim() ? session.runtime.trim() : null) ??
    readString(session.metadata, 'runtime') ??
    readString(session.context, 'runtime') ??
    readString(session.metadata, 'runtime_target') ??
    readString(session.context, 'runtime_target');

  if (raw) {
    const key = raw.toLowerCase();
    if (key === 'raios') return { key, label: 'RAIOS', external: false };
    if (key === 'openclaw') return { key, label: 'OpenClaw', external: true };
    return { key, label: raw, external: true };
  }

  // Proxy: canal CLI → runtime externo (OpenClaw). Demais → RAIOS.
  if (session.channel === 'cli') return { key: 'openclaw', label: 'OpenClaw', external: true };
  return { key: 'raios', label: 'RAIOS', external: false };
}

/** Origem da sessão (badge à direita no header). Fallback: Dashboard. */
export function deriveSessionOrigin(
  session: Pick<Session, 'metadata' | 'context'>,
): string {
  return (
    readString(session.metadata, 'origin') ??
    readString(session.context, 'origin') ??
    readString(session.context, 'source') ??
    'Dashboard'
  );
}

/**
 * Título exibido: `title` persistido (backend novo, §7.1) com degradação graceful
 * pro fallback atual (primeiras palavras da 1ª msg do user / "Conversa #id").
 */
export function sessionDisplayTitle(session: Session): string {
  const t = session.title?.trim();
  return t && t.length > 0 ? t : getSessionTitle(session);
}

/**
 * Resumo da sessão (sidebar direita, SESS-05). `summary` do payload novo;
 * fallback nos campos jsonb já existentes (metadata.summary / context.compaction_summary).
 */
export function sessionSummary(session: Session): string | null {
  const direct = session.summary?.trim();
  if (direct) return direct;
  return (
    readString(session.metadata, 'summary') ??
    readString(session.context, 'compaction_summary') ??
    readString(session.context, 'summary')
  );
}

/** "#a1b2c3d8…9f" — ID truncado dos cards/header (wireframe). */
export function shortSessionId(id: string): string {
  if (id.length <= 12) return `#${id}`;
  return `#${id.slice(0, 8)}…${id.slice(-2)}`;
}

export async function copySessionId(id: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(id);
    return true;
  } catch {
    return false;
  }
}
